import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  Trash2,
  Edit2,
  Download,
  Copy,
  Check,
  X,
  Save,
  Loader2,
  ShoppingCart,
  Printer,
  RotateCcw,
  ExternalLink,
  ChevronDown,
  QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;
const FRONTEND_URL = window.location.origin;

const TyreDetail = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [tire, setTire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(location.state?.editMode === true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editData, setEditData] = useState({});
  const [soldQuantity, setSoldQuantity] = useState(1);
  const [showExtraInfo, setShowExtraInfo] = useState(false);

  const userId = user?.UserId ? parseInt(user.UserId, 10) : 0;

  const fetchTire = async () => {
    if (!userId || userId === 0) {
      navigate("/login");
      return;
    }
    try {
      const response = await axios.get(`${API}/tires/${code}?user_id=${userId}`);
      setTire(response.data);
      setSoldQuantity(response.data.numero_pneumatici);
      setEditData({
        misura: response.data.misura,
        marca: response.data.marca,
        altezza_battistrada: response.data.altezza_battistrada,
        numero_pneumatici: response.data.numero_pneumatici,
        magazzino: response.data.magazzino,
        stato: response.data.stato || "Nuovo",
        stagione: response.data.stagione || "",
        nome_prodotto: response.data.nome_prodotto || "",
        classe_energetica: response.data.classe_energetica || "",
        codice_carico: response.data.codice_carico || "",
        codice_velocita: response.data.codice_velocita || "",
        selling_price: response.data.selling_price ?? null,
        purchase_price: response.data.purchase_price ?? null,
        eprelCode: response.data.eprel_code ?? null,
      });
    } catch (error) {
      toast.error("Pneumatico non trovato");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTire();
  }, [code]);

  const productUrl = `${FRONTEND_URL}/tyre/${code}`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Codice copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Errore nella copia");
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(productUrl);
      toast.success("URL copiato!");
    } catch (error) {
      toast.error("Errore nella copia");
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById("qr-code-svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement("a");
      link.download = `qr-${code}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrintLabel = async () => {
    try {
      const response = await axios.get(`${API}/tires/${code}/label`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `etichetta-${code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Etichetta scaricata");
    } catch (error) {
      toast.error("Errore nel download dell'etichetta");
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditData({
      misura: tire.misura,
      marca: tire.marca,
      altezza_battistrada: tire.altezza_battistrada,
      numero_pneumatici: tire.numero_pneumatici,
      magazzino: tire.magazzino,
      stato: tire.stato || "Nuovo",
      stagione: tire.stagione || "",
      nome_prodotto: tire.nome_prodotto || "",
      classe_energetica: tire.classe_energetica || "",
      codice_carico: tire.codice_carico || "",
      codice_velocita: tire.codice_velocita || "",
      selling_price: tire.selling_price ?? null,
      purchase_price: tire.purchase_price ?? null,
      eprelCode: tire.eprel_code ?? null,
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const { eprelCode, ...rest } = editData;
      const payload = {
        ...rest,
        eprel_code: eprelCode,
      };
      await axios.put(`${API}/tires/${code}?user_id=${userId}`, payload);
      await fetchTire();
      setEditing(false);
      toast.success("Pneumatico aggiornato");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const handleSell = async () => {
    try {
      const response = await axios.post(`${API}/tires/${code}/sell?user_id=${userId}`, {
        sold_date: new Date().toISOString(),
        numero_pneumatici: soldQuantity
      });
      
      if (response.data.partial_sale) {
        toast.success(`Vendute ${response.data.quantity_sold} gomme su ${response.data.quantity_sold + response.data.quantity_remaining}. Nuovo codice gomme vendute: ${response.data.sold_code}`);
        navigate("/");
      } else {
        toast.success("Pneumatico segnato come venduto");
        await fetchTire();
      }
    } catch (error) {
      toast.error("Errore nella vendita");
    }
  };

  const handleRestore = async () => {
    try {
      await axios.post(`${API}/tires/${code}/restore?user_id=${userId}`);
      toast.success("Pneumatico ripristinato in magazzino");
      await fetchTire();
    } catch (error) {
      toast.error("Errore nel ripristino");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tires/${code}?user_id=${userId}`);
      toast.success("Pneumatico eliminato");
      navigate("/");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]:
        name === "numero_pneumatici"
          ? parseInt(value) || 0
          : name === "selling_price" || name === "purchase_price"
          ? value === "" ? null : parseFloat(value)
          : name === "eprelCode"
          ? value === "" ? null : parseInt(value) || null
          : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!tire) {
    return null;
  }

  return (
    <div data-testid="tyre-detail" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate(tire.sold ? "/sold" : "/")}
            className="h-12 w-12 p-0"
          >
            <ArrowLeft size={24} />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={`text-2xl font-bold tracking-tight font-mono ${tire.sold ? "text-blue-400" : "text-primary"}`}>
                {tire.code}
              </h1>
              {tire.sold && (
                <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                  Venduto
                </Badge>
              )}
              {tire.stato && (
                <Badge variant="secondary" className={tire.stato === "Nuovo" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                  {tire.stato}
                </Badge>
              )}
              {tire.stagione && (
                <Badge variant="outline">{tire.stagione}</Badge>
              )}
              <Button
                data-testid="copy-code-btn"
                variant="ghost"
                size="sm"
                onClick={handleCopyCode}
                className="h-8 w-8 p-0"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{tire.nome_prodotto || tire.marca}</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {editing ? (
            <>
              <Button
                data-testid="cancel-edit-btn"
                variant="secondary"
                onClick={handleCancelEdit}
                className="h-10"
              >
                <X size={16} className="mr-2" />
                Annulla
              </Button>
              <Button
                data-testid="save-edit-btn"
                onClick={handleSaveEdit}
                disabled={saving}
                className="h-10 bg-primary text-primary-foreground"
              >
                {saving ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Save size={16} className="mr-2" />
                )}
                Salva
              </Button>
            </>
          ) : (
            <>
              {!tire.sold ? (
                <>
                  <Button
                    data-testid="edit-btn"
                    variant="secondary"
                    onClick={handleEdit}
                    className="h-10"
                  >
                    <Edit2 size={16} className="mr-2" />
                    Modifica
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-testid="sell-btn"
                        variant="secondary"
                        className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ShoppingCart size={16} className="mr-2" />
                        Venduto
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Segna come venduto</AlertDialogTitle>
                        <AlertDialogDescription>
                          Specifica quante gomme sono state vendute.
                          {soldQuantity < tire.numero_pneumatici 
                            ? " Le gomme rimanenti resteranno in magazzino."
                            : " Questo pneumatico verrà spostato nell'archivio venduti."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-4">
                        <Label htmlFor="sold-quantity" className="text-sm font-medium mb-2 block">
                          Quantità venduta
                        </Label>
                        <Input
                          id="sold-quantity"
                          type="number"
                          min="1"
                          max={tire.numero_pneumatici}
                          value={soldQuantity}
                          onChange={(e) => setSoldQuantity(parseInt(e.target.value))}
                          className="w-full"
                          data-testid="sold-quantity-input"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Vendi {soldQuantity} di {tire.numero_pneumatici} gomme
                        </p>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setSoldQuantity(tire.numero_pneumatici)}>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="confirm-sell-btn"
                          onClick={handleSell}
                          className="bg-blue-600 text-white hover:bg-blue-700"
                          disabled={!soldQuantity || soldQuantity < 1 || soldQuantity > tire.numero_pneumatici}
                        >
                          Conferma Vendita
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid="restore-btn"
                      variant="secondary"
                      className="h-10"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Ripristina
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Ripristina pneumatico</AlertDialogTitle>
                      <AlertDialogDescription>
                        Vuoi riportare questo pneumatico in magazzino?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleRestore}
                        className="bg-primary text-primary-foreground"
                      >
                        Ripristina
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="delete-btn"
                    variant="destructive"
                    className="h-10"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Elimina
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
                    <AlertDialogDescription>
                      Sei sicuro di voler eliminare questo pneumatico? Questa azione
                      non può essere annullata.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-delete-btn"
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Elimina
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Photo */}
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-0">
            {tire.photo_url ? (
              <img
                data-testid="tyre-photo"
                src={tire.photo_url}
                alt="Foto pneumatico"
                className="w-full aspect-video object-cover"
              />
            ) : (
              <div className="w-full aspect-video bg-secondary flex items-center justify-center">
                <p className="text-muted-foreground">Nessuna foto disponibile</p>
              </div>
            )}
          </CardContent>
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => setShowExtraInfo(!showExtraInfo)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
            >
              <span className="font-medium">Info aggiuntive</span>
              <ChevronDown 
                size={18} 
                className={`transition-transform duration-200 ${showExtraInfo ? 'rotate-180' : ''}`}
              />
            </button>
            {showExtraInfo && (
              <div className="px-4 pb-4 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Codice EPREL
                  </Label>
                  {editing ? (
                    <>
                      <Input
                        name="eprelCode"
                        type="number"
                        placeholder="es. 590129"
                        value={editData.eprelCode ?? ""}
                        onChange={handleEditChange}
                        className="h-10 bg-input/50 border-input font-mono"
                      />
                      {editData.eprelCode && (
                        <a
                          href={`https://eprel.ec.europa.eu/qr/${editData.eprelCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <QrCode size={12} />
                          Visualizza su EPREL →
                        </a>
                      )}
                    </>
                  ) : tire.eprel_code ? (
                    <>
                      <p className="font-mono text-lg">{tire.eprel_code}</p>
                      <a
                        href={`https://eprel.ec.europa.eu/qr/${tire.eprel_code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <QrCode size={12} />
                        Visualizza su EPREL →
                      </a>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">-</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* QR Code */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">QR Code</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <div className="qr-container mb-4">
              <QRCodeSVG
                id="qr-code-svg"
                value={productUrl}
                size={180}
                level="H"
                includeMargin
              />
            </div>
            <p className={`font-mono text-xl font-bold mb-2 tracking-widest ${tire.sold ? "text-blue-400" : "text-primary"}`}>
              {tire.code}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopyUrl}
              className="text-xs text-muted-foreground mb-4"
            >
              <ExternalLink size={12} className="mr-1" />
              Copia link prodotto
            </Button>
            <div className="flex gap-2 w-full">
              <Button
                data-testid="download-qr-btn"
                variant="secondary"
                onClick={handleDownloadQR}
                className="flex-1 h-12"
              >
                <Download size={18} className="mr-2" />
                QR
              </Button>
              <Button
                data-testid="print-label-btn"
                variant="secondary"
                onClick={handlePrintLabel}
                className="flex-1 h-12"
              >
                <Printer size={18} className="mr-2" />
                Etichetta
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Informazioni</CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Stato
                </Label>
                <Select value={editData.stato} onValueChange={(v) => handleSelectChange("stato", v)}>
                  <SelectTrigger className="h-12 bg-input/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nuovo">Nuovo</SelectItem>
                    <SelectItem value="Usato">Usato</SelectItem>
                    <SelectItem value="Deposito">Deposito</SelectItem>
                    <SelectItem value="Registro PFU">Registro PFU</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Stagione
                </Label>
                <Select value={editData.stagione} onValueChange={(v) => handleSelectChange("stagione", v)}>
                  <SelectTrigger className="h-12 bg-input/50">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Estivo">Estivo</SelectItem>
                    <SelectItem value="Invernale">Invernale</SelectItem>
                    <SelectItem value="4 Stagioni">4 Stagioni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Nome Prodotto
                </Label>
                <Input
                  name="nome_prodotto"
                  value={editData.nome_prodotto}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Marca
                </Label>
                <Input
                  name="marca"
                  data-testid="edit-marca"
                  value={editData.marca}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Misura
                </Label>
                <Input
                  name="misura"
                  data-testid="edit-misura"
                  value={editData.misura}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Classe Energetica
                </Label>
                <Select value={editData.classe_energetica} onValueChange={(v) => handleSelectChange("classe_energetica", v)}>
                  <SelectTrigger className="h-12 bg-input/50">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cod. Carico
                </Label>
                <Input
                  name="codice_carico"
                  value={editData.codice_carico}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cod. Velocità
                </Label>
                <Input
                  name="codice_velocita"
                  value={editData.codice_velocita}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Altezza Battistrada
                </Label>
                <Input
                  name="altezza_battistrada"
                  data-testid="edit-altezza"
                  value={editData.altezza_battistrada}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Quantità
                </Label>
                <Input
                  name="numero_pneumatici"
                  data-testid="edit-numero"
                  type="number"
                  min="1"
                  value={editData.numero_pneumatici}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Magazzino
                </Label>
                <Input
                  name="magazzino"
                  data-testid="edit-magazzino"
                  value={editData.magazzino}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Prezzo Vendita
                </Label>
                <Input
                  name="selling_price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="es. 49.90"
                  value={editData.selling_price ?? ""}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Prezzo Acquisto
                </Label>
                <Input
                  name="purchase_price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="es. 35.00"
                  value={editData.purchase_price ?? ""}
                  onChange={handleEditChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Misura
                </p>
                <p className="font-mono text-lg" data-testid="detail-misura">
                  {tire.misura}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Marca
                </p>
                <p className="text-lg" data-testid="detail-marca">
                  {tire.marca}
                </p>
              </div>
              {tire.nome_prodotto && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Nome Prodotto
                  </p>
                  <p className="text-lg">{tire.nome_prodotto}</p>
                </div>
              )}
              {tire.classe_energetica && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Classe Energetica
                  </p>
                  <Badge variant="outline" className="text-lg font-bold">{tire.classe_energetica}</Badge>
                </div>
              )}
              {tire.codice_carico && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Cod. Carico / Velocità
                  </p>
                  <p className="font-mono text-lg">
                    {tire.codice_carico}{tire.codice_velocita}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Altezza Battistrada
                </p>
                <p className="font-mono text-lg" data-testid="detail-altezza">
                  {tire.altezza_battistrada || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Quantità
                </p>
                <p className="font-mono text-lg" data-testid="detail-numero">
                  {tire.numero_pneumatici}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Magazzino
                </p>
                <p className="text-lg" data-testid="detail-magazzino">
                  {tire.magazzino}
                </p>
              </div>
              {tire.selling_price != null && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Prezzo Vendita
                  </p>
                  <p className="font-mono text-lg">
                    € {Number(tire.selling_price).toFixed(2)}
                  </p>
                </div>
              )}
              {tire.purchase_price != null && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Prezzo Acquisto
                  </p>
                  <p className="font-mono text-lg">
                    € {Number(tire.purchase_price).toFixed(2)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Data Registrazione
                </p>
                <p className="text-muted-foreground" data-testid="detail-date">
                  {new Date(tire.created_at).toLocaleString("it-IT")}
                </p>
              </div>
              {tire.sold && tire.sold_date && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-1">
                    Data Vendita
                  </p>
                  <p className="text-blue-400 font-medium" data-testid="detail-sold-date">
                    {new Date(tire.sold_date).toLocaleString("it-IT")}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TyreDetail;
